"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from "@mui/material";

import { useLocale, useTranslations } from "next-intl";
import { Control, Controller, UseFormSetValue } from "react-hook-form";
import { Locale } from "@/i18n/routing";
import { ColletteButton } from "@collette-travel/collette-common-controls";
import { BrochureDisplay } from "@/types/dto/common/brochure";
import getStatesByLocale from "@/config/states-config";
import AutoCompleteAddressField from "@/components/forms/auto-complete-address-field/auto-complete-address-field";
import useAutocompleteAddress from "@/hooks/forms/use-autocomplete-address";
import { RABFormData } from "@/types/leads/lead-forms";

interface RABFormProps {
  control: Control<RABFormData, any>;
  handleSubmit: React.FormEventHandler<HTMLFormElement> | undefined;
  brochureDisplay: BrochureDisplay;
  isInvalid: boolean;
  isSubmitting: boolean;
  defaultValues?: Partial<RABFormData>;
  setValue: UseFormSetValue<RABFormData>;
}
const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="none">
    <path
      d="M16.6666 10.833H10.8333V16.6663H9.16665V10.833H3.33331V9.16634H9.16665V3.33301H10.8333V9.16634H16.6666V10.833Z"
      fill="#2E844B"
    />
  </svg>
);

const AdvisorBrochureForm = ({
  control,
  handleSubmit,
  isInvalid,
  isSubmitting,
  brochureDisplay,
  defaultValues,
  setValue
}: RABFormProps) => {
  const r = useTranslations("FORMS.RAB");
  const t = useTranslations("FORMS");

  const locale = useLocale() as Locale;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const maximumOrder = brochureDisplay?.maximumOrder ?? 10; // Default to 10 if not provided

  const [showAddressLine2, setShowAddressLine2] = React.useState(false);
  const stateOptions = useMemo(
    () => getStatesByLocale(locale).map((state) => state.name),
    [locale]
  );

  const [mailOption, setMailOption] = useState(defaultValues ? "1" : "2");

  // When switching to "Address on file", populate form with defaultValues
  useEffect(() => {
    if (mailOption === "1" && defaultValues) {
      (Object.keys(defaultValues) as Array<keyof RABFormData>).forEach((key) => {
        setValue(key, defaultValues[key] as RABFormData[typeof key]);
      });
    }
    if (mailOption === "2") {
      setValue("firstName", "");
      setValue("lastName", "");
      setValue("address", "");
      setValue("address2", "");
      setValue("city", "");
      setValue("state", "");
      setValue("zipCode", "");
    }
  }, [mailOption, defaultValues, setValue]);

  // Handler for radio group change — updates the selected mail option
  const handleMailOptionChange = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, value: string) => {
      setMailOption(value);
    },
    []
  );

  // const shrinkReCaptcha = useMediaQuery("(max-width:425px)");

  const { handleChange: handleAddressChange, addressOptions } = useAutocompleteAddress();

  return (
    <Box
      component="form"
      noValidate
      onSubmit={handleSubmit}
      data-testid="advisor-rab-form"
      data-form-name="Request a Brochure"
      data-form-id="request-a-brochure-form"
      data-track="visibility"
    >
      <Stack gap="2.4rem" padding={{ xs: "2.4rem", md: "4rem" }}>
        <Stack
          sx={{
            flexDirection: { xs: "column", md: "row" },
            alignItems: "flex-start",
            gap: "8rem"
          }}
        >
          <Stack
            sx={{
              gap: "4rem",
              width: { xs: "100%", md: "50%" }
            }}
          >
            <Stack>
              <Typography variant="h6" data-testid="advisor-brochure-mail-label">
                Mail To:
              </Typography>
              <FormControl>
                <RadioGroup
                  sx={{ pl: "1.6rem" }}
                  value={mailOption}
                  onChange={handleMailOptionChange}
                >
                  {/* Option 1: use the address already on file */}
                  {/* Show the on-file address details only when option 1 is selected */}
                  {defaultValues && (
                    <>
                      <FormControlLabel value="1" control={<Radio />} label="Address on file" />
                      <Stack>
                        <Typography variant="body2" data-testid="advisor-brochure-address-line1">
                          {defaultValues?.address}, {defaultValues?.city}
                        </Typography>
                        <Typography variant="body2" data-testid="advisory-brochure-address-line2">
                          {defaultValues?.firstName}, {defaultValues?.phone}
                        </Typography>
                      </Stack>
                    </>
                  )}
                  {/* Option 2: enter a different delivery address */}
                  <FormControlLabel value="2" control={<Radio />} label="Alternate Address" />
                  {mailOption === "2" && (
                    <Stack gap="1.6rem" sx={{ mt: 1 }}>
                      {/* First Name */}
                      <Controller
                        name="firstName"
                        control={control}
                        rules={{
                          required: mailOption === "2" && t("Please complete this required field")
                        }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            required
                            data-testid="rab-first-name-field"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            aria-label={t("First Name")}
                            placeholder={t("First Name")}
                            label={t("First Name")}
                            sx={{
                              width: "100%",
                              "& .MuiOutlinedInput-root": { borderRadius: "1rem" }
                            }}
                          />
                        )}
                      />
                      {/* Last Name */}
                      <Controller
                        name="lastName"
                        control={control}
                        rules={{
                          required: mailOption === "2" && t("Please complete this required field")
                        }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            required
                            data-testid="rab-last-name-field"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            aria-label={t("Last Name")}
                            placeholder={t("Last Name")}
                            label={t("Last Name")}
                            sx={{
                              width: "100%",
                              "& .MuiOutlinedInput-root": { borderRadius: "1rem" }
                            }}
                          />
                        )}
                      />

                      {/* AUTOCOMPLETE ADDRESS FIELD */}
                      {locale === "en-au" && (
                        <Controller
                          name="experianAddress"
                          control={control}
                          rules={{
                            required: t("Please complete this required field"),
                            validate: (value) =>
                              addressOptions
                                .map((o) => o.suggestion)
                                .includes(value?.suggestion ?? "") ||
                              t("Please select a valid address from the list")
                          }}
                          render={({ field, fieldState }) => (
                            <AutoCompleteAddressField
                              testId="rab-autocomplete-address"
                              handleChange={handleAddressChange}
                              addressOptions={addressOptions}
                              field={field}
                              fieldState={fieldState}
                              borderRadius="1rem"
                            />
                          )}
                        />
                      )}
                      {locale !== "en-au" && (
                        <>
                          {/* Street Address */}
                          <Controller
                            name="address"
                            control={control}
                            rules={{
                              required:
                                mailOption === "2" && t("Please complete this required field")
                            }}
                            render={({ field, fieldState }) => (
                              <TextField
                                {...field}
                                required
                                autoComplete="address-line1"
                                data-testid="rab-address-field"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                aria-label={t("Street Address")}
                                placeholder={t("Street Address")}
                                label={t("Street Address")}
                                sx={{
                                  width: "100%",
                                  "& .MuiOutlinedInput-root": { borderRadius: "1rem" }
                                }}
                              />
                            )}
                          />
                          {showAddressLine2 ? (
                            <Controller
                              name="address2"
                              control={control}
                              render={({ field, fieldState }) => (
                                <TextField
                                  {...field}
                                  autoComplete="address-line2"
                                  data-testid="rab-address-line-2-field"
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                  aria-label={t("Street Address Line 2")}
                                  placeholder={t("Street Address Line 2")}
                                  label={t("Street Address Line 2")}
                                  sx={{
                                    width: "100%",
                                    "& .MuiOutlinedInput-root": { borderRadius: "1rem" }
                                  }}
                                />
                              )}
                            />
                          ) : (
                            <Box
                              onClick={() => setShowAddressLine2(true)}
                              sx={{
                                cursor: "pointer",
                                color: "secondary.main",
                                display: "flex",
                                alignItems: "center"
                              }}
                            >
                              <PlusIcon />
                              <Box component="span" sx={{ textDecoration: "underline", ml: 0.5 }}>
                                {t("Add address line 2")}
                              </Box>
                            </Box>
                          )}

                          {/* Country */}
                          <Controller
                            name="country"
                            control={control}
                            rules={{
                              required:
                                mailOption === "2" && t("Please complete this required field")
                            }}
                            render={({ field, fieldState }) => (
                              <TextField
                                {...field}
                                required
                                disabled
                                data-testid="rab-country-field"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                aria-label={t("Country")}
                                placeholder={t("Country")}
                                label={t("Country")}
                                sx={{
                                  width: "100%",
                                  "& .MuiOutlinedInput-root": { borderRadius: "1rem" }
                                }}
                              />
                            )}
                          />
                          {/* City */}
                          <Controller
                            name="city"
                            control={control}
                            rules={{
                              required:
                                mailOption === "2" && t("Please complete this required field")
                            }}
                            render={({ field, fieldState }) => (
                              <TextField
                                {...field}
                                required
                                data-testid="rab-city-field"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                aria-label={t("City")}
                                placeholder={t("City")}
                                label={t("City")}
                                sx={{
                                  width: "100%",
                                  "& .MuiOutlinedInput-root": { borderRadius: "1rem" }
                                }}
                              />
                            )}
                          />
                          {/* State */}
                          <Controller
                            name="state"
                            control={control}
                            rules={{
                              required:
                                mailOption === "2" && t("Please complete this required field"),
                              validate: (value) =>
                                mailOption !== "2" ||
                                stateOptions.includes(value) ||
                                t("Please select a valid state from the list")
                            }}
                            render={({ field, fieldState }) => (
                              <Autocomplete
                                {...field}
                                data-testid="rab-state-field_autocomplete"
                                value={field?.value}
                                options={stateOptions}
                                onInputChange={async (_, value) => {
                                  if (!value) return;
                                  field?.onChange(value);
                                }}
                                onChange={(_, newValue) => {
                                  // When user selects an option
                                  field?.onChange(newValue);
                                }}
                                noOptionsText={
                                  <Typography color="text.primary">
                                    {t("Please enter a valid state")}
                                  </Typography>
                                }
                                renderInput={(params) => (
                                  <TextField
                                    data-testid="rab-state-field_text-field"
                                    {...params}
                                    autoComplete="state"
                                    required
                                    label={t("State")}
                                    error={!!fieldState?.error}
                                    helperText={fieldState?.error?.message}
                                    aria-label={t("State")}
                                    placeholder={t("State")}
                                    sx={{
                                      width: "100%",
                                      "& .MuiOutlinedInput-root": { borderRadius: "1rem" }
                                    }}
                                  />
                                )}
                              />
                            )}
                          />
                          {/* Zip Code */}
                          <Controller
                            name="zipCode"
                            control={control}
                            rules={{
                              required:
                                mailOption === "2" && t("Please complete this required field")
                            }}
                            render={({ field, fieldState }) => (
                              <TextField
                                {...field}
                                required
                                data-testid="rab-zip-code-field"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                aria-label={t("Zip Code")}
                                placeholder={t("Zip Code")}
                                label={t("Zip Code")}
                                sx={{
                                  width: "100%",
                                  "& .MuiOutlinedInput-root": { borderRadius: "1rem" }
                                }}
                              />
                            )}
                          />
                        </>
                      )}
                    </Stack>
                  )}
                </RadioGroup>
              </FormControl>
            </Stack>
          </Stack>
          {/* Right column — list of brochures the user has selected with quantities */}
          <Stack spacing="1.2rem" flex={1}>
            <Typography variant="h6" data-testid="advisory-brochure-selected-label">
              {r("Selected Brochures")}
            </Typography>
            {/* <Typography variant="body2" data-testid="advisory-brochure-max-note">
              {r("Max brochures per order", { maximumOrder })}
            </Typography> */}
            <Stack spacing="0.8rem" sx={{ pl: "1.6rem" }}>
              {brochureDisplay.brochureOrder?.map((brochure: any, index: number) => (
                <Typography
                  key={brochure?.brochureCode ?? index}
                  variant="body2"
                  data-testid={`advisory-brochure-selected-${index}`}
                  sx={{ fontWeight: 500 }}
                >
                  {/* Quantity shown in bold, followed by the brochure name */}
                  <span style={{ fontWeight: "bold" }}>({brochure.brochureQuantity})</span>{" "}
                  {brochure.brochureName}
                </Typography>
              ))}
            </Stack>
          </Stack>
        </Stack>
        {/* OTHER INFO */}

        {/* VALIDATION MESSAGE */}
        {isInvalid && mailOption === "2" && (
          <Box
            sx={{
              padding: "0.4rem 1rem",
              backgroundColor: "error.100",
              borderRadius: "1rem",
              color: "error.dark"
            }}
          >
            <Typography>{t("Please complete all required fields")}</Typography>
          </Box>
        )}

        {/* SUBMIT BUTTON */}
        <Box
          width="100%"
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent={{ xs: "center", sm: "flex-end" }}
          alignItems="center"
          gap="1.6rem"
        >
          {/* <Controller
            name="recaptchaToken"
            control={control}
            rules={{
              required: t("Please complete this required field")
            }}
            render={({ field, fieldState }) => (
              <FormControl error={!!fieldState.error}>
                <Box
                  sx={{
                    overflow: "hidden",
                    width: shrinkReCaptcha ? `${300 * 0.7}px` : "auto",
                    height: shrinkReCaptcha ? `${74 * 0.7}px` : "auto"
                  }}
                >
                  <Box
                    sx={{
                      transformOrigin: "0 0",
                      transform: `scale(${shrinkReCaptcha ? "0.7" : "1"})`
                    }}
                  >
                    <ManagedReCAPTCHA
                      size="normal"
                      sitekey="6LfV8zIpAAAAALFDQIb2-f4xUO3PrI8vVhrgwA9x"
                      onChange={(token) => field.onChange(token)}
                    />
                  </Box>
                </Box>
                <FormHelperText>{fieldState.error?.message}</FormHelperText>
              </FormControl>
            )}
          /> */}
          <ColletteButton
            label={r("Request Brochure")}
            disabled={isSubmitting}
            aria-label={r("Request Brochure")}
            type="submit"
            variant="contained"
            color="secondary"
            size="large"
            testId="rab-submit-button"
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default AdvisorBrochureForm;
